import {
  AscProjectAllocationAverage,
  AscProjectMonthAllocation,
  generateMonthYearInRange,
  MonthYearPeriod,
  updateAllocationsInPeriod,
} from '@common/neo.tax';
import { useMemo } from 'react';

import { AscProjectMonthPerContributor } from '@neo.tax/asc/hooks/useContributor';
import { ContributorAllocationsInPeriod, ContributorProjectAllocation } from '@neo.tax/asc/types';

const monthYearKey = (year: number, month: number): string => `${year}-${month}`;

const projectMonthYearKey = (projectId: string, year: number, month: number): string =>
  `${projectId}-${monthYearKey(year, month)}`;

export const useContributorMonthBreakdowns = ({
  allocationsInPeriod,
  period,
}: {
  allocationsInPeriod: ContributorAllocationsInPeriod | null;
  period: MonthYearPeriod;
}): {
  monthYearAllocationsInPeriod: AscProjectMonthAllocation[][];
  periodAllocation: { ascProjectId: string; allocationPercent: number; useAllocationPercent: number }[];
} => {
  const monthYearAllocationsInPeriod = useMemo(() => {
    if (!allocationsInPeriod) {
      return [];
    }

    const monthYears = generateMonthYearInRange(period);

    // Get all project allocations from state
    const { projectIdToProjectAllocations, selectedIdToProjectAllocations } = allocationsInPeriod.projectPercents;

    const allProjectAllocations = [
      ...selectedIdToProjectAllocations.values(),
      ...projectIdToProjectAllocations.values(),
    ];

    // Build map of project month contributors from the allocations in state
    const projectMonthToProjectMonthContributor = new Map<string, AscProjectMonthPerContributor>();

    const projectAllocationEntries: Array<[string, ContributorProjectAllocation]> = [
      ...selectedIdToProjectAllocations,
      ...projectIdToProjectAllocations,
    ];

    projectAllocationEntries.forEach(([projectId, projectAllocation]) => {
      projectAllocation.projectMonthsPerContributor.forEach((projectPerContributor: AscProjectMonthPerContributor) => {
        projectMonthToProjectMonthContributor.set(
          projectMonthYearKey(projectId, projectPerContributor.year, projectPerContributor.month),
          projectPerContributor,
        );
      });
    });

    // Create newAverages from all project allocations (including newly searched projects)
    const newAverages: AscProjectAllocationAverage[] = allProjectAllocations.map(
      ({ allocationPercent, projectId }) => ({
        allocationPercent,
        ascProjectId: projectId,
      }),
    );

    // Build month allocations for all projects with allocations
    const ascProjectIdToPmas = allProjectAllocations.reduce(
      (acc: Record<string, AscProjectMonthAllocation[]>, allocation) => {
        const projectId = allocation.projectId;
        const pmas = monthYears.map((monthYear) => {
          const projectMonthAllocation = projectMonthToProjectMonthContributor.get(
            projectMonthYearKey(projectId, monthYear.year, monthYear.month),
          );

          if (projectMonthAllocation) {
            return {
              ...projectMonthAllocation,
              useAllocationPercent: projectMonthAllocation.useAllocationPercent,
              ascProjectId: projectId,
            };
          }
          return {
            ...monthYear,
            allocationPercent: 0,
            useAllocationPercent: 0,
            ascProjectId: projectId,
          };
        });
        acc[projectId] = pmas;
        return acc;
      },
      Object.create(null),
    );

    // Calculate total to check if allocations are valid for month breakdown calculation
    const totalAllocationPercent =
      newAverages.reduce((sum, avg) => sum + avg.allocationPercent, 0) +
      allocationsInPeriod.nonProjectPercent.allocationPercent;

    // Only update month breakdowns if total is 100% (or very close due to rounding)
    // This prevents errors when user is actively adjusting sliders
    if (Math.abs(totalAllocationPercent - 100) < 0.01) {
      const { newAscProjectIdToPmas } = updateAllocationsInPeriod({
        ascProjectIdToPmas,
        newAverages,
        newNonProjectTime: allocationsInPeriod.nonProjectPercent.allocationPercent,
      });
      return Object.values(newAscProjectIdToPmas);
    }
    // Return empty if total doesn't equal 100%
    return [];
  }, [allocationsInPeriod, period]);

  const periodAllocation = useMemo(() => {
    if (!allocationsInPeriod) {
      return [];
    }

    return [
      ...allocationsInPeriod.projectPercents.selectedIdToProjectAllocations.values(),
      ...allocationsInPeriod.projectPercents.projectIdToProjectAllocations.values(),
    ].map((value) => ({
      ascProjectId: value.projectId,
      allocationPercent: value.allocationPercent,
      useAllocationPercent: value.allocationPercent,
    }));
  }, [allocationsInPeriod]);

  return {
    monthYearAllocationsInPeriod,
    periodAllocation,
  };
};
